import { getUserByManager, getUserFilters } from "api/user";
import { useEffect, useMemo, useState } from "react";
import { useSharedState } from "state";
import { FilterValues } from "types/ui/filter";
import { User, UserFilters } from "types/user";

interface SidebarProps {
  filtersList: FilterValues;
  setFiltersList: React.Dispatch<React.SetStateAction<FilterValues>>;
  onFiltersReset: () => void;
  onFiltersSubmit: () => void;
}

export default function Filters({ filtersList, setFiltersList, onFiltersReset, onFiltersSubmit }: SidebarProps) {
  const {
    user,
    getUserData,
    users,
    getUsersData,
    templates,
    getTemplatesData,
    companies,
    getCompaniesData,
    departments,
    getDepartmentsData,
    levels,
    getLevelsData,
    isAdmin,
    filterQuery,
    notify,
  } = useSharedState();

  const [isFiltersOpened, setFiltersOpened] = useState<boolean>(false);
  const [myUsers, setMyUsers] = useState<User[]>([]);
  const [userFilters, setUserFilters] = useState<UserFilters | undefined>(undefined);

  useEffect(() => {
    const handleBackdropClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("offcanvas-backdrop")) {
        setFiltersOpened(false);
      }
    };

    document.addEventListener("click", handleBackdropClick);
    return () => document.removeEventListener("click", handleBackdropClick);
  }, []);

  const isTaskPage = useMemo((): boolean => {
    return window.location.pathname === "/tasks";
  }, [window.location.pathname]);

  const isDocumentPage = useMemo((): boolean => {
    return window.location.pathname === "/documents";
  }, [window.location.pathname]);

  const isPaymentPage = useMemo((): boolean => {
    return window.location.pathname === "/payments";
  }, [window.location.pathname]);

  const isBudgetPage = useMemo((): boolean => {
    return window.location.pathname === "/budgets";
  }, [window.location.pathname]);

  const isUserPage = useMemo((): boolean => {
    return window.location.pathname === "/users";
  }, [window.location.pathname]);

  const usersOptions = useMemo(() => {
    return [
      { label: "Выберите создателя", value: "", disabled: true },
      ...users.map((user) => ({ label: user.name, value: user.name, disabled: false })),
    ];
  }, [users]);

  const responsibleOptions = useMemo(() => {
    return [
      { label: "Выберите ответственного", value: "", disabled: true },
      ...myUsers.map((user) => ({ label: user.name, value: user.name, disabled: false })),
    ];
  }, [myUsers]);

  const templateOptions = useMemo(() => {
    return [
      { label: "Выберите шаблон", value: "", disabled: true },
      ...templates.map((template) => ({ label: template.name, value: template.name, disabled: false })),
    ];
  }, [templates]);

  const locationOptions = useMemo(() => {
    return [
      { label: "Выберите филиал", value: "", disabled: true },
      ...(isUserPage && userFilters ? userFilters.locations : user.locations).map((location) => ({
        label: location.location,
        value: isUserPage ? location.location_id : location.location,
        disabled: false,
      })),
    ];
  }, [isUserPage, userFilters, user.locations]);

  const companyOptions = useMemo(() => {
    return [
      { label: "Выберите компанию", value: "", disabled: true },
      ...companies.map((company) => ({ label: company.company, value: company.company_id, disabled: false })),
    ];
  }, [companies]);

  const departmentOptions = useMemo(() => {
    return [
      { label: "Выберите отдел", value: "", disabled: true },
      ...(isUserPage && userFilters ? userFilters.departments : departments).map((department) => ({
        label: department.department,
        value: department.department_id,
        disabled: false,
      })),
    ];
  }, [isUserPage, userFilters, departments]);

  const budgetOptions = useMemo(() => {
    return [
      { label: "Выберите бюджет", value: "", disabled: true },
      ...levels
        .filter((lvl) => lvl.parent_budget_type_id === null)
        .map((lvl) => ({
          label: lvl.budget_type!,
          value: lvl.budget_type_id!,
          disabled: false,
        })),
    ];
  }, [levels]);

  const roleOptions = useMemo(() => {
    return [
      { label: "Выберите роль", value: "", disabled: true },
      ...(userFilters?.roles.map((role) => ({ label: role.role, value: role.role, disabled: false })) || []),
    ];
  }, [userFilters]);

  useEffect(() => {
    isFiltersOpened && initFilters();
  }, [isFiltersOpened, isTaskPage, isDocumentPage, isPaymentPage]);

  const initFilters = async () => {
    isAdmin && !isUserPage && getUsersData();

    if (isTaskPage) {
      getUserByManagerData();
    } else if (isDocumentPage || isPaymentPage) {
      getTemplatesData();
      getUserData();
    } else if (isBudgetPage) {
      getCompaniesData();
      getDepartmentsData();
      getLevelsData();
    } else if (isUserPage) {
      getUserFiltersData();
    }
  };

  const getUserByManagerData = async () => {
    try {
      const res: User[] = await getUserByManager();
      setMyUsers(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении пользователей");
    }
  };

  const getUserFiltersData = async () => {
    try {
      const res: UserFilters = await getUserFilters();
      setUserFilters(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении фильтров");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFiltersList((prev) => ({ ...prev, [name]: value }));
  };

  const appliedFiltersCount = useMemo(() => {
    if (!filterQuery) return 0;
    return Object.values(filterQuery).filter((value) => value !== "" && value != null && value !== undefined).length;
  }, [filterQuery]);

  return (
    <>
      <a
        className="btn btn-ghost-primary grow position-relative"
        data-bs-toggle="offcanvas"
        href="#filters"
        role="button"
        aria-controls="filters"
        onClick={() => setFiltersOpened(true)}
      >
        Фильтры
        {appliedFiltersCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {appliedFiltersCount}
          </span>
        )}
      </a>

      <div className="offcanvas offcanvas-end" tabIndex={-1} id="filters" aria-labelledby="filtersLabel">
        <div className="offcanvas-header">
          <h2 className="offcanvas-title" id="filtersLabel">
            Фильтры
          </h2>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => setFiltersOpened(false)}
          ></button>
        </div>

        <div className="offcanvas-body">
          <div className="d-flex flex-column gap-3">
            {!isUserPage && (
              <div className="d-flex align-items-center row">
                <div className="col-5">Дата создания от</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="date"
                    name="from_date"
                    max="9999-12-31"
                    value={filtersList.from_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {!isUserPage && (
              <div className="d-flex align-items-center row">
                <div className="col-5">Дата создания до</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="date"
                    name="to_date"
                    max="9999-12-31"
                    value={filtersList.to_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Контрагент</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="text"
                    name="contragent"
                    value={filtersList.contragent}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Шаблон</div>
                <div className="col">
                  <select
                    className="form-select"
                    name="template_name"
                    value={filtersList.template_name}
                    onChange={handleInputChange}
                  >
                    {templateOptions.map((template, index) => (
                      <option key={index} value={template.value} disabled={template.disabled}>
                        {template.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {(isTaskPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Проект</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="text"
                    name="project"
                    value={filtersList.project}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {isAdmin && !isUserPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Создатель</div>
                <div className="col">
                  <select
                    className="form-select"
                    name={"creator_name"}
                    value={filtersList.creator_name}
                    onChange={handleInputChange}
                  >
                    {usersOptions.map((user, index) => (
                      <option key={index} value={user.value} disabled={user.disabled}>
                        {user.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {isTaskPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Ответственный</div>
                <div className="col">
                  <select
                    className="form-select"
                    name={isTaskPage ? "assigned_to_name" : "approver_name"}
                    value={isTaskPage ? filtersList.assigned_to_name : filtersList.approver_name}
                    onChange={handleInputChange}
                  >
                    {responsibleOptions.map((user, index) => (
                      <option key={index} value={user.value} disabled={user.disabled}>
                        {user.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage || isUserPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Филиал</div>
                <div className="col">
                  <select
                    className="form-select"
                    name={isUserPage ? "location_id" : "locations"}
                    value={isUserPage ? filtersList.location_id : filtersList.locations}
                    onChange={handleInputChange}
                  >
                    {locationOptions.map((user, index) => (
                      <option key={index} value={user.value} disabled={user.disabled}>
                        {user.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {isBudgetPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Компания</div>
                <div className="col">
                  <select
                    className="form-select"
                    name={"company_id"}
                    value={filtersList.company_id}
                    onChange={handleInputChange}
                  >
                    {companyOptions.map((company, index) => (
                      <option key={index} value={company.value} disabled={company.disabled}>
                        {company.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {(isBudgetPage || isUserPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Отдел</div>
                <div className="col">
                  <select
                    className="form-select"
                    name={"department_id"}
                    value={filtersList.department_id}
                    onChange={handleInputChange}
                  >
                    {departmentOptions.map((department, index) => (
                      <option key={index} value={department.value} disabled={department.disabled}>
                        {department.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {isBudgetPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Бюджет</div>
                <div className="col">
                  <select
                    className="form-select"
                    name={"budget_type_id"}
                    value={filtersList.budget_type_id}
                    onChange={handleInputChange}
                  >
                    {budgetOptions.map((budget, index) => (
                      <option key={index} value={budget.value} disabled={budget.disabled}>
                        {budget.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {isBudgetPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Год</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="text"
                    name="year"
                    value={filtersList.year}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {isUserPage && (
              <>
                <div className="d-flex align-items-center row">
                  <div className="col-4">Роль</div>
                  <div className="col">
                    <select className="form-select" name={"role"} value={filtersList.role} onChange={handleInputChange}>
                      {roleOptions.map((role, index) => (
                        <option key={index} value={role.value} disabled={role.disabled}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
            <button
              className="btn btn-ghost-primary"
              type="button"
              onClick={onFiltersReset}
              data-bs-dismiss="offcanvas"
            >
              Сбросить фильтры
            </button>
            <button className="btn btn-primary" type="button" onClick={onFiltersSubmit} data-bs-dismiss="offcanvas">
              Применить
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
